"use client";

import { useEffect, useState, useCallback } from "react";

const TOTAL = 18;

export function Slides() {
  const [i, setI] = useState(0);

  const go = useCallback((delta: number) => {
    setI((prev) => Math.max(0, Math.min(TOTAL - 1, prev + delta)));
  }, []);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault();
        go(+1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        go(-1);
      } else if (e.key === "Home") {
        setI(0);
      } else if (e.key === "End") {
        setI(TOTAL - 1);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [go]);

  const slide = SLIDES[i];

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden flex items-center justify-center p-8">
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 20% 0%, rgba(244, 63, 94, 0.10), transparent 50%), radial-gradient(circle at 80% 100%, rgba(167, 139, 250, 0.10), transparent 50%)`,
        }}
      />
      <div className="relative w-full max-w-5xl" key={i}>
        <div className="animate-slide-up">{slide}</div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 text-xs text-neutral-500">
        <button
          onClick={() => go(-1)}
          disabled={i === 0}
          className="rounded-md px-2 py-1 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          aria-label="Previous slide"
        >
          ←
        </button>
        <span className="tabular-nums">
          {String(i + 1).padStart(2, "0")} / {String(TOTAL).padStart(2, "0")}
        </span>
        <button
          onClick={() => go(+1)}
          disabled={i === TOTAL - 1}
          className="rounded-md px-2 py-1 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          aria-label="Next slide"
        >
          →
        </button>
      </div>

      <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5">
        <div
          className="h-full bg-white transition-all duration-300"
          style={{ width: `${((i + 1) / TOTAL) * 100}%` }}
        />
      </div>

      <div className="absolute top-6 right-8 text-[10px] uppercase tracking-widest text-neutral-600">
        RetailOS · {String(i + 1).padStart(2, "0")}
      </div>
    </div>
  );
}

const SLIDES: React.ReactNode[] = [
  // 00 — Cover
  <div key="00" className="text-center">
    <div className="mb-6 text-[11px] uppercase tracking-[0.3em] text-neutral-500">
      Working title · April 2026
    </div>
    <h1 className="text-[88px] leading-none font-semibold tracking-tight">RetailOS</h1>
    <p className="mt-8 text-2xl text-neutral-300 max-w-3xl mx-auto leading-snug">
      The operating system for modern retail.
    </p>
    <p className="mt-4 text-base text-neutral-500">
      Phase 1: vertical-agnostic POS. &nbsp;·&nbsp; Phase 2: the supplier network.
      &nbsp;·&nbsp; Phase 3: embedded financing.
    </p>
  </div>,

  // 01 — The problem
  <div key="01">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">01 · The problem</div>
    <h2 className="text-5xl font-semibold tracking-tight mb-10 max-w-4xl leading-tight">
      Retail commerce runs on broken silos.
    </h2>
    <div className="grid gap-4 md:grid-cols-2 text-[15px]">
      <Row label="Jewelers" detail="The Edge, Jewelry Shopkeeper — $800/mo, Windows-only, 2005 UX" />
      <Row label="Apparel" detail="Lightspeed Retail — $200/mo, generic, no vertical depth" />
      <Row label="F&B" detail="Toast — $200/mo/terminal, only serves restaurants" />
      <Row label="Wellness" detail="Mindbody — $195/mo, closed ecosystem" />
      <Row label="Wholesale" detail="Faire / Joor — separate platforms; no POS integration" />
      <Row label="Credit" detail="D&B / Experian — manual, slow, unaffordable for indies" />
    </div>
    <p className="mt-10 text-neutral-400 max-w-3xl">
      Each layer has its winner. Nobody owns the whole stack. That&apos;s the opportunity.
    </p>
  </div>,

  // 02 — The Idea
  <div key="02">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">02 · The idea</div>
    <h2 className="text-5xl font-semibold tracking-tight mb-8 leading-tight">
      One operating system.<br />
      <span className="text-neutral-400">Both sides of the transaction.</span>
    </h2>
    <p className="text-xl text-neutral-300 max-w-3xl leading-relaxed">
      Every retailer signs up, picks their vertical — jewelry, apparel, wellness, F&amp;B,
      anything we ship as a module — and runs their store on RetailOS. Then we connect them
      to their suppliers on the same platform. The data flywheel that creates is unlike
      anything else in B2B retail.
    </p>
    <div className="mt-10 grid gap-4 md:grid-cols-3">
      <BigStat n="Phase 1" label="Vertical POS" />
      <BigStat n="Phase 2" label="Supplier network" />
      <BigStat n="Phase 3" label="Embedded finance" />
    </div>
  </div>,

  // 03 — How it works (Phase 1)
  <div key="03">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
      03 · Phase 1 — Core platform
    </div>
    <h2 className="text-5xl font-semibold tracking-tight mb-8 leading-tight">
      Core + modules.
    </h2>
    <div className="grid gap-6 md:grid-cols-2">
      <div className="rounded-xl border border-white/10 bg-white/5 p-6">
        <h3 className="text-xl font-semibold mb-3">Core platform</h3>
        <p className="text-neutral-400 mb-4 text-sm">Everything every retailer needs.</p>
        <ul className="space-y-2 text-[15px] text-neutral-300">
          <li>• Multi-tenant auth + RBAC</li>
          <li>• Customer, Order, Payment primitives</li>
          <li>• AI conversational layer</li>
          <li>• Observability + audit + sign-in funnel</li>
          <li>• Stripe payments (Connect)</li>
          <li>• Branded public storefront</li>
        </ul>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/5 p-6">
        <h3 className="text-xl font-semibold mb-3">Vertical modules</h3>
        <p className="text-neutral-400 mb-4 text-sm">Pick one or many per merchant.</p>
        <ul className="space-y-2 text-[15px] text-neutral-300">
          <li>• Jewelry — layaway, repair, memo, appraisal</li>
          <li>• Apparel — size matrix, fitting room, returns</li>
          <li>• Wellness — appointments, packages, intake</li>
          <li>• F&amp;B — menu, kitchen display, reservations</li>
          <li>• + auto, pharmacy, cannabis, electronics…</li>
        </ul>
      </div>
    </div>
    <p className="mt-8 text-sm text-neutral-500 max-w-3xl">
      Each module ships capabilities, role presets, nav items, POS extensions, and AI agents.
      A new vertical is a ~200-line TypeScript file.
    </p>
  </div>,

  // 04 — Status
  <div key="04">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">04 · Where we are</div>
    <h2 className="text-5xl font-semibold tracking-tight mb-10 leading-tight">
      Working product, not a deck.
    </h2>
    <div className="grid gap-4 md:grid-cols-2">
      <Proof num="4" label="Vertical modules live" detail="Jewelry, Apparel, Wellness, F&B" />
      <Proof num="65" label="Routes shipping" detail="Dashboard + admin + platform views" />
      <Proof num="17" label="Role presets" detail="Owner, Bench Jeweler, Therapist, etc." />
      <Proof num="4" label="AI agents registered" detail="Clienteling, Merchandising, Concierge, Ops" />
      <Proof num="Shopify-like" label="Onboarding + install UI" detail="Self-service, zero code" />
      <Proof num="/s/{slug}" label="Public storefronts" detail="Branded per merchant" />
    </div>
    <p className="mt-10 text-neutral-400 max-w-3xl">
      Architecture proven. The platform is built and deployed; vertical modules ship live behind
      a single Shopify-style install flow.
    </p>
  </div>,

  // 05 — Phase 2 transition
  <div key="05" className="text-center">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
      Phase 2 · The bigger play
    </div>
    <h2 className="text-[64px] leading-none font-semibold tracking-tight">
      We don&apos;t just<br />
      <span className="text-neutral-500">run the store.</span>
    </h2>
    <p className="mt-10 text-2xl text-neutral-300 max-w-3xl mx-auto leading-snug">
      We connect the store to its <span className="text-white">suppliers</span> —
      and to <span className="text-white">capital</span>.
    </p>
    <p className="mt-8 text-base text-neutral-500 max-w-2xl mx-auto">
      Once we own the retailer&apos;s POS and the supplier&apos;s wholesale flow, we have data
      no other platform can replicate. Five reasons this is a $1B+ opportunity:
    </p>
  </div>,

  // 06 — Reason 1: Bilateral data moat
  <div key="06">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
      06 · Why it&apos;s huge — 1 of 5
    </div>
    <h2 className="text-5xl font-semibold tracking-tight mb-8 leading-tight">
      The bilateral data moat.
    </h2>
    <div className="grid gap-3 md:grid-cols-2 mb-8">
      <CompCard who="Faire" sees="Wholesale orders" gap="Doesn't see actual sell-through" />
      <CompCard who="Joor" sees="Apparel catalogs" gap="No POS integration" />
      <CompCard who="D&B / Experian" sees="Self-reported credit" gap="Slow, manual, irrelevant for indies" />
      <CompCard
        who="RetailOS"
        sees="Real sales velocity, reorder intent, payment behavior — both sides, in real time"
        gap="Nobody else can sell this signal"
        highlight
      />
    </div>
    <p className="text-neutral-400 max-w-3xl">
      Suppliers will pay to access what we see. That&apos;s a recurring SaaS line on top of
      transaction take-rate.
    </p>
  </div>,

  // 07 — Reason 2: Mutual trust scoring
  <div key="07">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
      07 · Why it&apos;s huge — 2 of 5
    </div>
    <h2 className="text-5xl font-semibold tracking-tight mb-8 leading-tight">
      Mutual trust scoring as the killer feature.
    </h2>
    <div className="grid gap-6 md:grid-cols-2 mb-8">
      <div className="rounded-xl border border-white/10 bg-white/5 p-6">
        <h3 className="text-lg font-semibold mb-3">For retailers</h3>
        <ul className="space-y-2 text-sm text-neutral-300">
          <li>• Pay-on-time score (0–100)</li>
          <li>• Annual wholesale GMV tier</li>
          <li>• Years on platform</li>
          <li>• Trade references auto-aggregated</li>
          <li>• Dispute rate (with resolution-in-favor weighting)</li>
        </ul>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/5 p-6">
        <h3 className="text-lg font-semibold mb-3">For suppliers</h3>
        <ul className="space-y-2 text-sm text-neutral-300">
          <li>• On-time delivery score</li>
          <li>• Quality score (1 − return rate)</li>
          <li>• Responsiveness (hours to reply)</li>
          <li>• Cancellation rate</li>
          <li>• Vertical specialty + certifications</li>
        </ul>
      </div>
    </div>
    <p className="text-neutral-400 max-w-3xl">
      Real-time, behavior-based, reciprocal. Once an indie retailer has 24 months of platform
      credit history they can&apos;t get from D&amp;B,
      <span className="text-white"> switching costs become enormous. The moat compounds over years.</span>
    </p>
  </div>,

  // 08 — Reason 3: Vertical depth
  <div key="08">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
      08 · Why it&apos;s huge — 3 of 5
    </div>
    <h2 className="text-5xl font-semibold tracking-tight mb-8 leading-tight">
      Vertical-specific supply chain depth.
    </h2>
    <div className="space-y-3 mb-6">
      <VerticalNuance
        name="Jewelry"
        color="#D4A437"
        depth="Memo terms · GIA certs · conflict-free certifications · lab-grown vs natural · blockchain provenance"
      />
      <VerticalNuance
        name="Apparel"
        color="#10B981"
        depth="Size matrix orders · seasonal drops 6 months out · sample requests · returns by reason"
      />
      <VerticalNuance
        name="Wellness"
        color="#A78BFA"
        depth="Licensure verification · expiration date FIFO · retail vs professional grade · subscription replenishment"
      />
      <VerticalNuance
        name="F&B"
        color="#F97316"
        depth="Spot pricing · daily delivery · perishability + HACCP · contract vs spot · local farm CSAs"
      />
    </div>
    <p className="text-neutral-400 max-w-3xl">
      Horizontal competitors will never go this deep across all four. We ship one module at a time.
    </p>
  </div>,

  // 09 — Reason 4: Embedded financing
  <div key="09">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
      09 · Why it&apos;s huge — 4 of 5
    </div>
    <h2 className="text-5xl font-semibold tracking-tight mb-8 leading-tight">
      Embedded financing as the endgame.
    </h2>
    <div className="rounded-xl border border-white/10 bg-white/5 p-6 mb-6">
      <h3 className="text-base font-semibold text-white mb-3">The factoring loop</h3>
      <ol className="space-y-2 text-sm text-neutral-300 list-decimal list-inside">
        <li>Supplier ships goods, issues invoice with Net 60 terms</li>
        <li>Platform pays supplier in full <span className="text-white">today</span></li>
        <li>Retailer pays platform on day 60</li>
        <li>Platform keeps 1–3% spread</li>
      </ol>
    </div>
    <div className="grid gap-4 md:grid-cols-3">
      <Stat3 n="$1T+" label="US B2B trade credit market" />
      <Stat3 n="1–3%" label="Platform spread per transaction" />
      <Stat3 n="Highest" label="margin business in B2B fintech" />
    </div>
    <p className="mt-8 text-neutral-400 max-w-3xl">
      Underwriting requires data. We have it: transaction history + credit scores + velocity, all
      proprietary. <span className="text-white">No one else can underwrite as accurately.</span>
    </p>
  </div>,

  // 10 — Reason 5: Network effects compound across verticals
  <div key="10">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
      10 · Why it&apos;s huge — 5 of 5
    </div>
    <h2 className="text-5xl font-semibold tracking-tight mb-8 leading-tight">
      Network effects that compound across verticals.
    </h2>
    <div className="rounded-xl border border-white/10 bg-white/3 p-6 mb-6 text-sm leading-relaxed text-neutral-300">
      <p className="mb-3">
        A merchant runs <span className="text-white">jewelry + spa</span> on RetailOS. Their spa
        supplier joins to keep the relationship alive.
      </p>
      <p className="mb-3">
        That spa supplier discovers <span className="text-white">another merchant</span> on the
        platform who also runs a spa. They reach out, win the account.
      </p>
      <p className="mb-3">
        That second merchant <span className="text-white">also runs apparel</span>. Suddenly the
        apparel supplier of <em>that</em> merchant is exposed to spa merchants nationwide.
      </p>
      <p className="text-white text-base">
        One retailer joining lights up multiple supplier networks.
      </p>
    </div>
    <p className="text-neutral-400 max-w-3xl">
      Faire is one network. Joor is one network. RetailOS becomes a graph of overlapping
      vertical networks tied together by a shared merchant base — a pattern single-vertical
      platforms physically cannot match.
    </p>
  </div>,

  // 11 — Architecture
  <div key="11">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">11 · How it&apos;s built</div>
    <h2 className="text-5xl font-semibold tracking-tight mb-8 leading-tight">
      One codebase. Two products. Shared data.
    </h2>
    <div className="rounded-xl border border-white/10 bg-white/5 p-6 mb-6 font-mono text-xs text-neutral-300 leading-relaxed overflow-x-auto">
      <div>retailos/</div>
      <div className="ml-4">apps/web/        <span className="text-neutral-500"># retailers (Phase 1, live)</span></div>
      <div className="ml-4">apps/supply/     <span className="text-amber-300"># suppliers (Phase 2)</span></div>
      <div className="ml-4">packages/core/   <span className="text-neutral-500"># modules · RBAC · AI</span></div>
      <div className="ml-4">packages/db/     <span className="text-neutral-500"># shared schema</span></div>
      <div className="ml-4">packages/ui/     <span className="text-neutral-500"># Pure Dark design system</span></div>
      <div className="ml-4">packages/module-jewelry/, module-apparel/, module-wellness/, module-fnb/</div>
      <div className="ml-4 text-amber-300">packages/supply-core/   # PO/Invoice/Credit primitives</div>
      <div className="ml-4 text-amber-300">packages/supply-jewelry/, supply-apparel/, ...</div>
    </div>
    <p className="text-neutral-400 max-w-3xl">
      <code className="text-white">Organization.type</code> = RETAILER | SUPPLIER | HYBRID separates
      the UX shells. Same auth, same billing, same data layer.
      <span className="text-white"> Phase 2 schema is already merged in dev.</span>
    </p>
  </div>,

  // 12 — Market
  <div key="12">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">12 · Market</div>
    <h2 className="text-5xl font-semibold tracking-tight mb-8 leading-tight">
      Retail + B2B trade = $5T+.
    </h2>
    <div className="grid gap-4 md:grid-cols-3 mb-8">
      <TAMCard range="$5T" label="US retail spend" sub="Annual, all verticals" />
      <TAMCard range="$1T+" label="US B2B trade credit" sub="The factoring market" />
      <TAMCard range="$85B" label="Retail SaaS" sub="Projected 2028, global" />
    </div>
    <div className="text-neutral-300 text-[15px] max-w-3xl leading-relaxed">
      <p>Comparable companies and what they captured:</p>
      <ul className="mt-3 space-y-1.5 text-neutral-400">
        <li>• <span className="text-white">Shopify</span> ($90B at peak) — only e-commerce</li>
        <li>• <span className="text-white">Faire</span> ($12B valuation) — only B2B wholesale (apparel/gifts)</li>
        <li>• <span className="text-white">Toast</span> ($24B at IPO) — only F&amp;B POS</li>
        <li>• <span className="text-white">Stripe</span> ($91B) — only payments</li>
      </ul>
      <p className="mt-4 text-white">
        RetailOS combines all four wedges into one platform. Even 0.5% capture is a $25B+ company.
      </p>
    </div>
  </div>,

  // 13 — Network flywheel
  <div key="13">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">13 · The flywheel</div>
    <h2 className="text-5xl font-semibold tracking-tight mb-10 leading-tight">
      How it compounds.
    </h2>
    <div className="space-y-3 text-sm">
      <FlowStep n={1} text="Retailer joins RetailOS for POS" />
      <FlowStep n={2} text="Discovers supplier marketplace, invites their existing suppliers" />
      <FlowStep n={3} text="Supplier joins, sees their existing customer + adjacency" />
      <FlowStep n={4} text="Supplier reaches out to other retailers in same vertical" />
      <FlowStep n={5} text="More retailers → more demand signal → AI gets smarter" />
      <FlowStep n={6} text="Better terms negotiated based on credit scores" />
      <FlowStep n={7} text="Trade financing becomes available — platform takes spread" highlight />
      <FlowStep n={8} text="Platform becomes default trading venue for the vertical" highlight />
      <FlowStep n={9} text="Repeat in the next vertical, accelerated by the shared merchant base" />
    </div>
  </div>,

  // 14 — Go-to-market
  <div key="14">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">14 · Go-to-market</div>
    <h2 className="text-5xl font-semibold tracking-tight mb-10 leading-tight">
      Wedge with depth, expand with the network.
    </h2>
    <div className="space-y-6">
      <Phase n="Phase 1" title="Retailer base — months 0–12">
        Land 20 jewelers as the wedge vertical, then add apparel or wellness in months 6–9 to
        prove the module abstraction works across categories.
        <span className="text-white"> $500k ARR target.</span>
      </Phase>
      <Phase n="Phase 2" title="Supplier ecosystem — months 6–18">
        Launch <code className="text-amber-300">supply.retailos.app</code>. Onboard 30+ suppliers
        through the retailers we&apos;ve already won. Expand by vertical. Credit scoring goes live
        at 6 months of transaction history.
        <span className="text-white"> $10M ARR target by month 18.</span>
      </Phase>
      <Phase n="Phase 3" title="Embedded finance — months 18–36">
        Partner with Slope or Resolve for factoring. Eventually pursue bank charter.
        <span className="text-white"> $50M+ ARR target by Y3.</span>
      </Phase>
    </div>
  </div>,

  // 15 — Team
  <div key="15">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">15 · Team</div>
    <h2 className="text-5xl font-semibold tracking-tight mb-10 leading-tight">
      Builders, not theorists.
    </h2>
    <div className="space-y-5 text-[15px] text-neutral-300 max-w-3xl leading-relaxed">
      <p>
        A founding team that has built and scaled retail and supplier platforms before, including
        the POS system that Intuit white-labeled at the heart of QuickBooks Commerce.
      </p>
      <p>
        We&apos;ve seen exactly how the buy-side / sell-side split breaks down at scale and where
        the network effects live. RetailOS is the second-time-around bet, with a much bigger swing.
      </p>
      <p>
        Architecture, design system, capability-based RBAC, module registry, AI agent framework,
        and bilateral PO workflow are all live in the platform you&apos;re looking at right now.
      </p>
    </div>
  </div>,

  // 16 — The Ask
  <div key="16">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
      16 · What we&apos;re looking for
    </div>
    <h2 className="text-5xl font-semibold tracking-tight mb-10 leading-tight">
      Thought partners, first customers, strategic capital.
    </h2>
    <div className="grid gap-4 md:grid-cols-3">
      <AskCard
        label="Thought partners"
        detail="CEOs / operators who've scaled platforms or B2B marketplaces. Help pressure-test the supplier ecosystem strategy."
      />
      <AskCard
        label="First customers"
        detail="Indie retailers running 1–5 locations. Free for 6 months. Onboarded in under a day."
      />
      <AskCard
        label="Seed capital"
        detail="$4–6M to ship Phase 2 supplier marketplace + credit scoring + financing partnership in 18 months."
      />
    </div>
    <div className="mt-12 p-6 rounded-xl border border-white/10 bg-white/5 max-w-3xl">
      <p className="text-sm text-neutral-400 mb-2">Live demo at</p>
      <p className="text-xl">
        <span className="text-white font-medium">retailos.musedart.com</span> — try it as a merchant.
      </p>
      <p className="mt-3 text-sm text-neutral-500">
        30-min walkthrough: onboarding → vertical demo → POS sale → admin panel → Phase 2 schema preview.
      </p>
    </div>
  </div>,

  // 17 — Close
  <div key="17" className="text-center">
    <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-8">Thank you</div>
    <h1 className="text-[80px] leading-none font-semibold tracking-tight">RetailOS</h1>
    <p className="mt-10 text-2xl text-neutral-300 max-w-3xl mx-auto leading-snug">
      The platform that connects the store, the supplier, and the capital —
      <span className="text-white"> all in one place.</span>
    </p>
    <p className="mt-10 text-sm text-neutral-500">Let&apos;s build it together.</p>
    <div className="mt-12 text-xs text-neutral-600 tabular-nums">
      Working name · final brand deferred until launch
    </div>
  </div>,
];

// ─── Reusable slide parts ────────────────────────────────────

function Row({ label, detail }: { label: string; detail: string }) {
  return (
    <div className="flex items-baseline gap-4 py-3 border-b border-white/8">
      <div className="w-32 flex-shrink-0 font-medium">{label}</div>
      <div className="text-neutral-400">{detail}</div>
    </div>
  );
}

function BigStat({ n, label }: { n: string; label: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-8 text-center">
      <div className="text-3xl font-semibold tracking-tight">{n}</div>
      <div className="mt-2 text-xs uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}

function Proof({ num, label, detail }: { num: string; label: string; detail: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <div className="text-3xl font-semibold tracking-tight">{num}</div>
      <div className="mt-1 font-medium">{label}</div>
      <div className="text-sm text-neutral-500 mt-0.5">{detail}</div>
    </div>
  );
}

function CompCard({
  who,
  sees,
  gap,
  highlight,
}: {
  who: string;
  sees: string;
  gap: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-5 ${
        highlight ? "border-amber-300/40 bg-amber-300/5" : "border-white/10 bg-white/5"
      }`}
    >
      <div className={`font-semibold ${highlight ? "text-amber-200" : "text-white"}`}>{who}</div>
      <div className="text-xs text-neutral-500 mt-1.5 uppercase tracking-widest">Sees</div>
      <div className="text-sm text-neutral-300 mt-1">{sees}</div>
      <div className="text-xs text-neutral-500 mt-3 uppercase tracking-widest">Gap</div>
      <div className="text-sm text-neutral-400 mt-1">{gap}</div>
    </div>
  );
}

function VerticalNuance({
  name,
  color,
  depth,
}: {
  name: string;
  color: string;
  depth: string;
}) {
  return (
    <div className="flex items-baseline gap-5 rounded-xl border border-white/8 bg-white/3 px-5 py-4">
      <div
        className="w-28 flex-shrink-0 font-semibold tracking-wide uppercase text-xs"
        style={{ color }}
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full mr-2" style={{ background: color }} />
        {name}
      </div>
      <div className="text-sm text-neutral-300 leading-relaxed">{depth}</div>
    </div>
  );
}

function Stat3({ n, label }: { n: string; label: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <div className="text-3xl font-semibold tracking-tight">{n}</div>
      <div className="text-xs text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

function TAMCard({
  range,
  label,
  sub,
}: {
  range: string;
  label: string;
  sub: string;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-6">
      <div className="text-4xl font-semibold tracking-tight">{range}</div>
      <div className="mt-2 font-medium">{label}</div>
      <div className="text-sm text-neutral-500 mt-0.5">{sub}</div>
    </div>
  );
}

function FlowStep({
  n,
  text,
  highlight,
}: {
  n: number;
  text: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-start gap-4">
      <div
        className={`w-7 h-7 flex-shrink-0 rounded-full border flex items-center justify-center text-xs font-semibold ${
          highlight
            ? "border-amber-300/40 bg-amber-300/10 text-amber-200"
            : "border-white/15 bg-white/5"
        }`}
      >
        {n}
      </div>
      <div className={highlight ? "text-white" : "text-neutral-300"}>{text}</div>
    </div>
  );
}

function Phase({
  n,
  title,
  children,
}: {
  n: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-5 items-start">
      <div className="w-32 flex-shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{n}</div>
      </div>
      <div>
        <div className="font-semibold mb-1 text-lg">{title}</div>
        <div className="text-neutral-400 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function AskCard({ label, detail }: { label: string; detail: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{label}</div>
      <p className="text-neutral-300 leading-relaxed text-sm">{detail}</p>
    </div>
  );
}
